import React, { useState, useEffect, useCallback } from "react";
import { Heading, Subheading } from "../../../../design/heading";
import { Text } from "../../../../design/text";
import { Button } from "../../../../design/button";
import { Input } from "../../../../design/input";
import { Select } from "../../../../design/select";
import { Textarea } from "../../../../design/textarea";
import { Field, Label } from "../../../../design/fieldset";
import { Badge } from "../../../../design/badge";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import {
  FunctioningAssessment,
  AssessmentType,
  AssessmentCategory,
  AssessmentDimension,
  DimensionRating,
  ASSESSMENT_TYPE_LABELS,
  ASSESSMENT_CATEGORY_LABELS,
  ASSESSMENT_DIMENSION_LABELS,
  CATEGORY_DIMENSIONS,
} from "@visitproof/types";
import { AssessmentService } from "../../../../services/AssessmentService";
import { useAuth } from "../../../../contexts/AuthContext";
import { useAccount } from "../../../../contexts/AccountContext";
import { formatDateUTC, parseDateToTimestamp, todayLocalDateString } from "../../../../utils/date";
import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid";

interface Props {
  caseId: string;
}

const RATING_LABELS: Record<number, string> = {
  1: "Very Poor",
  2: "Poor",
  3: "Fair",
  4: "Good",
  5: "Excellent",
};

const RatingInput: React.FC<{
  dimension: AssessmentDimension;
  value: number;
  onChange: (value: number) => void;
}> = ({ dimension, value, onChange }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 min-w-[160px]">
      {ASSESSMENT_DIMENSION_LABELS[dimension]}
    </span>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          key={rating}
          type="button"
          onClick={() => onChange(rating)}
          className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${
            value === rating
              ? "bg-blue-600 text-white shadow-sm"
              : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600"
          }`}
          title={RATING_LABELS[rating]}
        >
          {rating}
        </button>
      ))}
    </div>
    <span className="text-xs text-zinc-400 w-16 text-right">
      {value > 0 ? RATING_LABELS[value] : ""}
    </span>
  </div>
);

const AssessmentCard: React.FC<{
  assessment: FunctioningAssessment;
  onDelete: (id: string) => void;
}> = ({ assessment, onDelete }) => {
  const getRatingForDimension = (dim: AssessmentDimension): number => {
    const found = assessment.ratings.find((r) => r.dimension === dim);
    return found?.rating ?? 0;
  };

  const avgRating =
    assessment.ratings.length > 0
      ? (assessment.ratings.reduce((sum, r) => sum + r.rating, 0) / assessment.ratings.length).toFixed(1)
      : "N/A";

  return (
    <div className="p-5 border rounded-lg border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Badge color={assessment.type === "baseline" ? "blue" : "green"}>
              {ASSESSMENT_TYPE_LABELS[assessment.type]}
            </Badge>
            <Text className="text-sm text-zinc-500">
              {formatDateUTC(assessment.assessmentDate, "long")}
            </Text>
          </div>
          <Text className="text-xs text-zinc-400 mt-1">
            Assessed by {assessment.assessedByName || "Unknown"} &middot; Avg: {avgRating}/5
          </Text>
        </div>
        <Button plain onClick={() => onDelete(assessment.id)}>
          <TrashIcon className="w-4 h-4 text-zinc-400 hover:text-red-500" />
        </Button>
      </div>

      {(Object.entries(CATEGORY_DIMENSIONS) as [AssessmentCategory, AssessmentDimension[]][]).map(
        ([category, dimensions]) => (
          <div key={category} className="mb-3">
            <Text className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">
              {ASSESSMENT_CATEGORY_LABELS[category]}
            </Text>
            <div className="grid grid-cols-3 gap-2">
              {dimensions.map((dim) => {
                const rating = getRatingForDimension(dim);
                return (
                  <div key={dim} className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                        rating >= 4
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : rating >= 3
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          : rating > 0
                          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          : "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500"
                      }`}
                    >
                      {rating || "-"}
                    </div>
                    <Text className="text-xs text-zinc-600 dark:text-zinc-400">
                      {ASSESSMENT_DIMENSION_LABELS[dim]}
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      {assessment.notes && (
        <Text className="text-sm text-zinc-500 mt-3 italic">{assessment.notes}</Text>
      )}
    </div>
  );
};

const AssessmentsTab: React.FC<Props> = ({ caseId }) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [assessments, setAssessments] = useState<FunctioningAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [assessmentType, setAssessmentType] = useState<AssessmentType>("baseline");
  const [assessmentDate, setAssessmentDate] = useState(
    todayLocalDateString()
  );
  const [notes, setNotes] = useState("");
  const [ratings, setRatings] = useState<Record<AssessmentDimension, number>>(() => {
    const initial: Record<string, number> = {};
    Object.values(CATEGORY_DIMENSIONS)
      .flat()
      .forEach((dim) => {
        initial[dim] = 0;
      });
    return initial as Record<AssessmentDimension, number>;
  });

  const loadAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AssessmentService.getAssessments(caseId);
      setAssessments(data);
    } catch (err) {
      console.error("Error loading assessments:", err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  // Auto-set type based on existing assessments
  useEffect(() => {
    if (assessments.length > 0) {
      setAssessmentType("follow_up");
    }
  }, [assessments]);

  const resetForm = () => {
    setAssessmentType(assessments.length > 0 ? "follow_up" : "baseline");
    setAssessmentDate(todayLocalDateString());
    setNotes("");
    const initial: Record<string, number> = {};
    Object.values(CATEGORY_DIMENSIONS)
      .flat()
      .forEach((dim) => {
        initial[dim] = 0;
      });
    setRatings(initial as Record<AssessmentDimension, number>);
  };

  const handleSave = async () => {
    if (!user || !account) return;
    setSaving(true);
    try {
      const dimensionRatings: DimensionRating[] = Object.entries(ratings)
        .filter(([, rating]) => rating > 0)
        .map(([dimension, rating]) => ({
          dimension: dimension as AssessmentDimension,
          rating,
        }));

      await AssessmentService.createAssessment(caseId, {
        caseId,
        accountId: account.id,
        type: assessmentType,
        assessmentDate: parseDateToTimestamp(assessmentDate)!,
        ratings: dimensionRatings,
        notes: notes || undefined,
        assessedBy: user.uid,
        assessedByName: user.displayName || user.email || "Unknown",
      });

      setIsDialogOpen(false);
      resetForm();
      await loadAssessments();
    } catch (err) {
      console.error("Error saving assessment:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assessmentId: string) => {
    try {
      await AssessmentService.deleteAssessment(caseId, assessmentId);
      await loadAssessments();
    } catch (err) {
      console.error("Error deleting assessment:", err);
    }
  };

  const handleRatingChange = (dimension: AssessmentDimension, value: number) => {
    setRatings((prev) => ({ ...prev, [dimension]: value }));
  };

  if (loading) {
    return <Text>Loading assessments...</Text>;
  }

  // Find baseline and latest follow-up for comparison
  const baseline = assessments.find((a) => a.type === "baseline");
  const latestFollowUp = assessments.find((a) => a.type === "follow_up");
  const showComparison = baseline && latestFollowUp;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Subheading>Functioning Assessments</Subheading>
          <Text className="text-sm text-zinc-500">
            Rate parent-child relationship, child functioning, and parent functioning on a 1-5 scale.
          </Text>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          New Assessment
        </Button>
      </div>

      {/* Pre/Post Comparison */}
      {showComparison && (
        <div className="p-5 border rounded-lg border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10">
          <Subheading className="mb-3">Baseline vs. Latest Follow-Up</Subheading>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {(Object.entries(CATEGORY_DIMENSIONS) as [AssessmentCategory, AssessmentDimension[]][]).map(
              ([category, dimensions]) => {
                const baselineAvg = dimensions.reduce((sum, dim) => {
                  const r = baseline.ratings.find((r) => r.dimension === dim);
                  return sum + (r?.rating ?? 0);
                }, 0) / dimensions.length;
                const followUpAvg = dimensions.reduce((sum, dim) => {
                  const r = latestFollowUp.ratings.find((r) => r.dimension === dim);
                  return sum + (r?.rating ?? 0);
                }, 0) / dimensions.length;
                const change = followUpAvg - baselineAvg;

                return (
                  <div key={category} className="text-center">
                    <Text className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                      {ASSESSMENT_CATEGORY_LABELS[category]}
                    </Text>
                    <div className="flex items-center justify-center gap-3">
                      <div>
                        <Text className="text-2xl font-bold text-zinc-400">{baselineAvg.toFixed(1)}</Text>
                        <Text className="text-xs text-zinc-400">Baseline</Text>
                      </div>
                      <Text className="text-xl text-zinc-300">&rarr;</Text>
                      <div>
                        <Text className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{followUpAvg.toFixed(1)}</Text>
                        <Text className="text-xs text-zinc-400">Latest</Text>
                      </div>
                    </div>
                    <Text
                      className={`text-sm font-medium mt-1 ${
                        change > 0
                          ? "text-green-600 dark:text-green-400"
                          : change < 0
                          ? "text-red-600 dark:text-red-400"
                          : "text-zinc-400"
                      }`}
                    >
                      {change > 0 ? "+" : ""}{change.toFixed(1)} change
                    </Text>
                    <div className="mt-2 space-y-1">
                      {dimensions.map((dim) => {
                        const bRating = baseline.ratings.find((r) => r.dimension === dim)?.rating ?? 0;
                        const fRating = latestFollowUp.ratings.find((r) => r.dimension === dim)?.rating ?? 0;
                        const dimChange = fRating - bRating;
                        return (
                          <div key={dim} className="flex items-center justify-between text-xs">
                            <span className="text-zinc-500">{ASSESSMENT_DIMENSION_LABELS[dim]}</span>
                            <span className={dimChange > 0 ? "text-green-600" : dimChange < 0 ? "text-red-600" : "text-zinc-400"}>
                              {bRating}&rarr;{fRating} ({dimChange > 0 ? "+" : ""}{dimChange})
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {assessments.length === 0 ? (
        <div className="text-center py-12">
          <Text className="text-zinc-400">No assessments recorded yet.</Text>
          <Text className="text-sm text-zinc-400 mt-1">
            Create a baseline assessment to start tracking functioning.
          </Text>
        </div>
      ) : (
        <div className="space-y-4">
          {assessments.map((assessment) => (
            <AssessmentCard
              key={assessment.id}
              assessment={assessment}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* New Assessment Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} size="2xl">
        <DialogTitle>New Functioning Assessment</DialogTitle>
        <DialogBody className="space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field>
              <Label>Assessment Type</Label>
              <Select
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value as AssessmentType)}
              >
                {Object.entries(ASSESSMENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Assessment Date</Label>
              <Input
                type="date"
                value={assessmentDate}
                onChange={(e) => setAssessmentDate(e.target.value)}
              />
            </Field>
          </div>

          {(Object.entries(CATEGORY_DIMENSIONS) as [AssessmentCategory, AssessmentDimension[]][]).map(
            ([category, dimensions]) => (
              <div key={category}>
                <Subheading className="mb-2">
                  {ASSESSMENT_CATEGORY_LABELS[category]}
                </Subheading>
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 space-y-1">
                  {dimensions.map((dim) => (
                    <RatingInput
                      key={dim}
                      dimension={dim}
                      value={ratings[dim]}
                      onChange={(val) => handleRatingChange(dim, val)}
                    />
                  ))}
                </div>
              </div>
            )
          )}

          <Field>
            <Label>Notes (Optional)</Label>
            <Textarea
              rows={3}
              value={notes}
              placeholder="Any observations or context about this assessment..."
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={handleSave}>
            {saving ? "Saving..." : "Save Assessment"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AssessmentsTab;
